// <s-spray>: a perfume "spritz". When a button sends it the `--spray` command it plays a
// synthesized spray sound (Web Audio, no file needed) and bursts a cone of mist particles
// above the button. Reduced-motion users get a gentle fade instead. One component per file.
class SSpray extends HTMLElement {
  connectedCallback() {
    this.addEventListener("command", (event) => {
      if (event.command !== "--spray") return;
      this.#sound();
      this.#mist();
    });
  }

  #sound() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    this.context ??= new AudioCtx();
    const ctx = this.context;
    if (ctx.state === "suspended") ctx.resume();

    const now = ctx.currentTime;
    const duration = 0.6;

    // White noise source for the hiss.
    const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    // The nozzle "click" of the pump.
    const click = ctx.createOscillator();
    const clickGain = ctx.createGain();
    click.type = "triangle";
    click.frequency.setValueAtTime(180, now);
    click.frequency.exponentialRampToValueAtTime(60, now + 0.05);
    clickGain.gain.setValueAtTime(0.12, now);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    click.connect(clickGain).connect(ctx.destination);
    click.start(now);
    click.stop(now + 0.08);

    // The fine mist hiss: bright, airy noise with a soft fade.
    const hiss = ctx.createBufferSource();
    hiss.buffer = buffer;
    const highpass = ctx.createBiquadFilter();
    highpass.type = "highpass";
    highpass.frequency.value = 2500;
    const air = ctx.createBiquadFilter();
    air.type = "peaking";
    air.frequency.value = 7000;
    air.gain.value = 6;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.16, now + 0.06);
    gain.gain.setValueAtTime(0.16, now + 0.22);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    hiss.connect(highpass).connect(air).connect(gain).connect(ctx.destination);
    hiss.start(now + 0.02);
    hiss.stop(now + duration);
  }

  #mist() {
    const layer = this.querySelector("[data-spray-layer]");
    if (!layer) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // A soft cloud that blooms and fades.
    const cloud = document.createElement("span");
    Object.assign(cloud.style, { position: "absolute", left: "50%", bottom: "100%", width: "7rem", height: "7rem", borderRadius: "9999px", background: "currentColor", opacity: "0", filter: "blur(24px)" });
    layer.append(cloud);
    cloud
      .animate(
        [
          { opacity: 0, transform: "translate(-50%, 20%) scale(0.3)" },
          { opacity: 0.35, transform: "translate(-50%, -10%) scale(1)", offset: 0.3 },
          { opacity: 0, transform: "translate(-50%, -40%) scale(1.6)" },
        ],
        { duration: reduce ? 900 : 1400, easing: "ease-out" },
      )
      .finished.then(() => cloud.remove());

    if (reduce) return;

    // Fine droplets spraying upward in a cone.
    const count = 42;
    for (let i = 0; i < count; i++) {
      const drop = document.createElement("span");
      const size = 2 + Math.random() * 5;
      Object.assign(drop.style, { position: "absolute", left: "50%", bottom: "100%", borderRadius: "9999px", background: "currentColor" });
      drop.style.width = `${size}px`;
      drop.style.height = `${size}px`;
      layer.append(drop);

      const angle = (-90 + (Math.random() - 0.5) * 70) * (Math.PI / 180);
      const distance = 60 + Math.random() * 140;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      const drift = (Math.random() - 0.5) * 30;

      drop
        .animate(
          [
            { opacity: 0.9, transform: "translate(-50%, 0) scale(1)" },
            { opacity: 0.6, transform: `translate(calc(-50% + ${x * 0.7}px), ${y * 0.7}px) scale(1)`, offset: 0.5 },
            { opacity: 0, transform: `translate(calc(-50% + ${x + drift}px), ${y - 20}px) scale(0.4)` },
          ],
          { duration: 700 + Math.random() * 700, delay: Math.random() * 120, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" },
        )
        .finished.then(() => drop.remove());
    }
  }
}

if (!customElements.get("s-spray")) customElements.define("s-spray", SSpray);
